import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { Utensils, Clock, Heart, Flame, ShoppingBag, BookOpen } from 'lucide-react';
import * as api from '../lib/api';
import { COVERS } from '../lib/media';
import { VIDEOS } from '../lib/media';
import HeroVideo from '../components/HeroVideo';

export default function Kitchen() {
  const [book, setBook] = useState<api.BookListItem | undefined>(undefined);

  useEffect(() => {
    api.books.list().then(list => {
      setBook(list.find(b => b.slug === 'fasting-carnivore-kitchen' || b.title?.toLowerCase().includes('kitchen')));
    }).catch(() => {});
  }, []);

  const coverSrc = book?.coverUrl || COVERS.kitchen;
  return (
    <>
      {/* Hero */}
      <section className="hero">
        <HeroVideo className="video-bg" src={VIDEOS.meatGrill} />
        <div className="video-overlay" />
        <div className="container book-page-hero-container">
          <motion.div
            className="hero-content text-center"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <span className="hero-label">Book Four</span>
            <h1>Fasting Carnivore Kitchen</h1>
            <p>Simple, Delicious Meat-Based Recipes That Make Carnivore Eating Exciting</p>
            <Link to="/join" className="btn btn-primary btn-lg">
              Join the Community
            </Link>
          </motion.div>
        </div>
      </section>

      {/* Pain Point */}
      <section className="section">
        <div className="container">
          <div className="content-narrow">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
            >
              <h2 className="heading-section text-center">
                Carnivore Doesn't Have to Be Boring
              </h2>
              <div className="text-body-lg">
                <p className="mb-responsive-lg">
                  You've heard it a hundred times: "Just eat steak and eggs." And while that works,
                  sometimes you want more. More variety. More flavor. More satisfaction. More excitement at mealtime.
                </p>
                <p className="mb-responsive-lg">
                  You don't need carbs to make food delicious. You don't need complicated recipes
                  with twenty ingredients. You just need to know how to cook meat properly—and discover how many incredible options you actually have.
                </p>
                <p className="mb-responsive-lg">
                  The truth is, most people quit carnivore not because it doesn't work—but because they got bored. They never learned how to make it delicious.
                </p>
                <p className="book-page-emphasis-accent">
                  Welcome to the Fasting Carnivore Kitchen. Boredom ends here.
                </p>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* What's Inside */}
      <section className="section section-light">
        <div className="container">
          <motion.div
            className="book-page-section-intro"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="heading-section">What's Inside</h2>
            <p>
              Everything you need to make carnivore eating delicious, simple, and sustainable for the long term. No culinary degree required.
            </p>
          </motion.div>

          <div className="grid-3 book-page-grid-xl">
            <RecipeCategory
              icon={<Flame size={32} />}
              title="Beef Recipes"
              description="Steaks, roasts, ground beef, and more. Master the art of cooking beef to perfection."
            />
            <RecipeCategory
              icon={<Utensils size={32} />}
              title="Pork Recipes"
              description="Bacon, chops, ribs, and belly. Discover the versatility of pork on carnivore."
            />
            <RecipeCategory
              icon={<Heart size={32} />}
              title="Organ Meats"
              description="Liver, heart, and more. The most nutrient-dense foods on the planet, made delicious."
            />
            <RecipeCategory
              icon={<Clock size={32} />}
              title="Quick Meals"
              description="Egg dishes and fast preparations for busy days when you need food now."
            />
            <RecipeCategory
              icon={<ShoppingBag size={32} />}
              title="Meal Prep Guide"
              description="Cook once, eat all week. Batch cooking strategies that save time and money."
            />
            <RecipeCategory
              icon={<BookOpen size={32} />}
              title="Quick Reference"
              description="Cooking times, temperatures, and tips. Everything at your fingertips."
            />
          </div>
        </div>
      </section>

      {/* The Book */}
      <section className="section">
        <div className="container">
          <motion.div
            className="book-page-section-intro"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <span className="section-label">The Book</span>
            <h2 className="heading-section">Fasting Carnivore Kitchen</h2>
          </motion.div>
          <div className="two-col book-page-two-col">
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="text-center"
            >
              <img
                src={coverSrc}
                alt="Fasting Carnivore Kitchen Book"
                className="book-page-cover"
              />
            </motion.div>
            <motion.div
              initial={{ opacity: 0, x: 20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
            >
              <h3 className="book-page-feature-heading">
                Your Complete Carnivore Cookbook
              </h3>
              <p className="text-body-lg book-page-body-spaced">
                From quick weeknight dinners to special occasion meals—every recipe you need to make
                carnivore eating delicious and sustainable for the long term. This is how you make meat-based eating something you actually look forward to.
              </p>
              <ul className="book-page-feature-list">
                <li>9 comprehensive chapters covering every type of protein</li>
                <li>Full recipes with nutritional info and pro tips</li>
                <li>Cooking times and temperatures for perfect results every time</li>
                <li>Meal planning worksheets to simplify your week</li>
                <li>Shopping list templates so you never forget what you need</li>
                <li>Quick reference guides for when you need answers fast</li>
              </ul>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Sample Recipes */}
      <section className="section section-light">
        <div className="container">
          <motion.div
            className="book-page-section-intro"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="heading-section">Sample Recipes</h2>
            <p>A taste of what's inside</p>
          </motion.div>

          <div className="grid-3 book-page-grid-lg">
            <RecipeCard
              title="Perfect Ribeye"
              time="25 min"
              description="Reverse-seared to perfection with a crispy crust and pink center."
            />
            <RecipeCard
              title="Crispy Pork Belly"
              time="3 hrs"
              description="Melt-in-your-mouth meat with crackling skin. Worth every minute."
            />
            <RecipeCard
              title="Beef Liver Pâté"
              time="45 min"
              description="Organ meats made delicious. Even liver haters love this one."
            />
            <RecipeCard
              title="Smash Burgers"
              time="10 min"
              description="Thin, crispy edges. Maximum flavor in minimum time."
            />
            <RecipeCard
              title="Bone Marrow"
              time="20 min"
              description="Nature's butter. Rich, savory, and incredibly satisfying."
            />
            <RecipeCard
              title="Carnivore Scramble"
              time="15 min"
              description="Eggs, bacon, and ground beef. The perfect quick meal."
            />
          </div>
        </div>
      </section>

      {/* Who This Is For */}
      <section className="section">
        <div className="container">
          <div className="content-narrow">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
            >
              <h2 className="heading-section text-center">
                This Is For You If...
              </h2>
              <div className="text-body-lg">
                <ul className="book-page-list-spaced">
                  <li>You want variety and excitement without leaving carnivore</li>
                  <li>You're bored of eating the same plain steak and eggs every day</li>
                  <li>You want to actually enjoy cooking again and improve your skills</li>
                  <li>You need quick meals for busy days but don't want to sacrifice quality</li>
                  <li>You want to batch cook and save time during the week</li>
                  <li>You're ready to fall in love with meat-based eating and stay consistent for life</li>
                </ul>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="cta">
        <div className="container text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2>Ready to Transform Your Kitchen?</h2>
            <p>Get instant access to all four books, workbooks, and a community of people who are making carnivore eating delicious together.</p>
            <Link to="/join" className="btn btn-primary btn-lg">
              Join the Community — $97 Lifetime
            </Link>
            <p className="book-page-cta-fine-subtle">
              All books included · Lifetime access · New recipes added regularly
            </p>
          </motion.div>
        </div>
      </section>
    </>
  );
}

function RecipeCategory({ icon, title, description }: { icon: React.ReactNode; title: string; description: string }) {
  return (
    <motion.div
      className="book-page-icon-card"
      initial={{ opacity: 0, y: 20 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
    >
      <div className="book-page-icon-card-icon">{icon}</div>
      <h3 className="book-page-icon-card-title">{title}</h3>
      <p className="book-page-icon-card-desc">{description}</p>
    </motion.div>
  );
}

function RecipeCard({ title, time, description }: { title: string; time: string; description: string }) {
  return (
    <motion.div
      className="book-page-recipe-card"
      initial={{ opacity: 0, y: 20 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
    >
      <div className="book-page-recipe-header">
        <h3 className="book-page-recipe-title">{title}</h3>
        <span className="book-page-recipe-time">
          <Clock size={14} />
          {time}
        </span>
      </div>
      <p className="book-page-recipe-desc">{description}</p>
    </motion.div>
  );
}
